// GoogleCompleteOrgPage — port of auth/google_complete_org.html. AuthShell split layout for Google signup: name + org join/create + domain.
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AuthShell, { AuthCardHeader, BrandLeft } from '@/components/auth/AuthShell';
import { authService } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { useToast } from '@/hooks/useToast';
import { paths } from '@/routes/paths';

export default function GoogleCompleteOrgPage() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const { error } = useToast();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') ?? 'you@example.com';

  const [form, setForm] = useState({
    firstName: searchParams.get('first') ?? '',
    lastName: searchParams.get('last') ?? '',
    orgId: '',
    orgName: '',
    mobileNumber: '',
    primaryDomain: '',
  });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { user, token, expiresIn } = await authService.googleCompleteOrg({ email, ...form });
      login(user, token, expiresIn);
      navigate(paths.landing);
    } catch {
      error('Could not complete signup', 'Please check your details and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      pageClass="signup-page"
      left={
        <BrandLeft
          topLine="Complete Profile"
          description="Complete your organization details to finish setting up your Quralyst account."
        />
      }
      cardHeader={
        <AuthCardHeader
          title="Account Details"
          subtitle={
            <>
              Signed in as <strong>{email}</strong>
            </>
          }
          right={<Link to={paths.auth.login}>Cancel</Link>}
        />
      }
    >
      <form className="auth-form" onSubmit={onSubmit} noValidate>
        <input type="hidden" name="email" value={email} />
        <div className="form-group">
          <label htmlFor="gc_first_name">First Name</label>
          <input
            id="gc_first_name"
            type="text"
            placeholder="Enter your first name"
            value={form.firstName}
            onChange={set('firstName')}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="gc_last_name">Last Name</label>
          <input
            id="gc_last_name"
            type="text"
            placeholder="Enter your last name"
            value={form.lastName}
            onChange={set('lastName')}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="gc_org_id">Organization ID (Optional join code)</label>
          <input
            id="gc_org_id"
            type="text"
            placeholder="QR-XXXXXX"
            value={form.orgId}
            onChange={set('orgId')}
          />
        </div>
        <div className="form-group">
          <label htmlFor="gc_org_name">New Organization Name</label>
          <input
            id="gc_org_name"
            type="text"
            placeholder="If creating a new organization"
            value={form.orgName}
            onChange={set('orgName')}
          />
        </div>
        <div className="form-group">
          <label htmlFor="gc_primary_domain">
            Company Domain <span className="text-danger">*</span>
          </label>
          <input
            id="gc_primary_domain"
            type="text"
            placeholder="company.com"
            value={form.primaryDomain}
            onChange={set('primaryDomain')}
          />
          <div className="form-text">Must match your Google account email&apos;s domain.</div>
        </div>
        <div className="form-group">
          <label htmlFor="gc_mobile">Mobile Number (Optional)</label>
          <input
            id="gc_mobile"
            type="tel"
            placeholder="Optional contact number"
            value={form.mobileNumber}
            onChange={set('mobileNumber')}
          />
        </div>
        <button type="submit" className="auth-button" disabled={submitting}>
          Continue
        </button>
      </form>
    </AuthShell>
  );
}
